"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PrivateRoomScene = void 0;
const ActionRoomScene_1 = require("./ActionRoomScene");
const interfaces_1 = require("../../../shared/interfaces");
const pixi_js_1 = require("pixi.js");
const i18next_1 = __importDefault(require("i18next"));
const blocks_1 = require("../blocks");
const Global_1 = require("../Global");
const SceneManager_1 = require("../managers/SceneManager");
const share_svg_1 = __importDefault(require("../../assets/svg/share.svg"));
const ui_1 = require("@pixi/ui");
const WigglyText_1 = require("../objects/WigglyText");
const settings_1 = require("../config/settings");
class PrivateRoomScene extends ActionRoomScene_1.ActionRoomScene {
    constructor(app, sceneManager, gameManager, _type = interfaces_1.GameType.BattleFriends) {
        super(app, sceneManager, gameManager, _type);
        this.started = false;
        this.readyButton = this.makeReadyButton();
        this.readyButton.visible = false;
        this.subs.add(this.gameManager.room$.subscribe({
            next: (room) => __awaiter(this, void 0, void 0, function* () {
                if (room && room.type === this.type) {
                    const container = (this.centerText.getChildByName('code'));
                    if (container) {
                        const code = (container.getChildAt(0));
                        if (code) {
                            if (code.text !== room.rkey) {
                                code.text = room.rkey;
                            }
                            if (!container.visible) {
                                yield (0, Global_1.fade)(container, 1, 0.5);
                            }
                        }
                    }
                }
            })
        }));
    }
    get waitingtext() {
        return i18next_1.default.t('battle.waiting');
    }
    setPlayer(atIndex, player = null) {
        const _super = Object.create(null, {
            setPlayer: { get: () => super.setPlayer }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.setPlayer.call(this, atIndex, player);
            yield (0, Global_1.fade)(this.readyButton, this.playerlist.filter((o) => o).length > 1 ? 1 : 0);
        });
    }
    makeReadyButton() {
        const button = new blocks_1.FillingButton(this.app, this.overSmallSize ? 140 : 120, this.overSmallSize ? 50 : 40, settings_1.Settings.whiteColor, settings_1.Settings.lightGrayColor, false);
        const style = blocks_1.Block.textStyle(this.overSmallSize ? 28 : 24);
        button.setText(i18next_1.default.t('battle.start'), style);
        const container = button.asButton();
        this.sigs.push(container.onPress.connect(() => {
            if (!this.started) {
                this.gameManager.notifyReady();
                button.setText(i18next_1.default.t('battle.waiting'));
                this.started = true;
            }
        }));
        return this.addChild(container);
    }
    joinRoom() {
        // May be already in the room on the previous screen
        const cur = this.gameManager.room$.value;
        if (!cur) {
            this.gameManager.enterRoom(this.type);
        }
    }
    makeCenterText() {
        var _a;
        const container = new pixi_js_1.Container();
        const txt = i18next_1.default.t('battle.invitecode');
        this.text = new WigglyText_1.WigglyText(this.app, txt);
        this.text.power = (0, Global_1.px)(7);
        this.text.anchor.set(0.5, 0);
        this.ttext = new pixi_js_1.Text(((_a = this.gameManager.room$.value) === null || _a === void 0 ? void 0 : _a.rkey) || null);
        this.ttext.anchor.set(0.5, 0);
        // Share button
        if (navigator === null || navigator === void 0 ? void 0 : navigator.share) {
            this.tshare = pixi_js_1.Sprite.from(share_svg_1.default);
            this.tshare.anchor.set(0.5);
            this.ttext.addChild(this.tshare);
        }
        this.sharebtn = new ui_1.ButtonContainer(this.ttext);
        this.sharebtn.name = 'code';
        this.sharebtn.visible = false;
        this.sigs.push(this.sharebtn.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
            var _b;
            const rkey = (_b = this.gameManager.room$.value) === null || _b === void 0 ? void 0 : _b.rkey;
            if ((navigator === null || navigator === void 0 ? void 0 : navigator.share) && rkey) {
                try {
                    yield navigator.share({
                        title: settings_1.Settings.title,
                        text: i18next_1.default.t('battle.share', { title: settings_1.Settings.title }),
                        url: process.env.SHARE_URL + '/#' + rkey,
                    });
                }
                catch (err) {
                    console.warn(err);
                }
            }
        })));
        container.addChild(this.text, this.sharebtn);
        return this.addChild(container);
    }
    enterScene(from, target) {
        const _super = Object.create(null, {
            enterScene: { get: () => super.enterScene }
        });
        return __awaiter(this, void 0, void 0, function* () {
            // Have a cool zoom animation
            if (target instanceof ui_1.ButtonContainer || target instanceof ui_1.Input) {
                yield this.animateMask(target);
            }
            return _super.enterScene.call(this, from);
        });
    }
    backTo() {
        return SceneManager_1.Scenes.Others;
    }
    layout() {
        var _a, _b;
        super.layout();
        this.sharebtn.y = this.text.height + (0, Global_1.px)(4);
        const tstyle = blocks_1.Block.textStyle(this.overSmallSize ? 50 : 40, false);
        tstyle.align = 'center';
        this.ttext.style = tstyle;
        (_a = this.tshare) === null || _a === void 0 ? void 0 : _a.scale.set((0, Global_1.px)(this.overSmallSize ? 0.24 : 0.2));
        (_b = this.tshare) === null || _b === void 0 ? void 0 : _b.position.set((0, Global_1.px)(this.overSmallSize ? 90 : 75), (0, Global_1.px)(this.overSmallSize ? 20 : 15));
        const center = this.app.screen.width / 2;
        this.readyButton.position.set(center, this.roomContainer.y + this.roomContainer.height + this.readyButton.height / 2 + (0, Global_1.px)(8));
    }
}
exports.PrivateRoomScene = PrivateRoomScene;
